import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Loader2,
  Car,
  Users,
  Briefcase,
  Plus,
  Pencil,
  Trash2,
  Save,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/partner/fleet")({
  component: FleetPage,
});

interface Offer {
  id: string;
  vehicle_category: string;
  car_model: string;
  from_label: string;
  to_label: string;
  from_lat: number;
  from_lng: number;
  to_lat: number;
  to_lng: number;
  from_iata: string | null;
  to_iata: string | null;
  max_passengers: number;
  max_luggage: number;
  price_amount: number;
  price_currency: string;
  distance_km: number;
  duration_min: number;
  active: boolean;
}

const CATEGORIES = [
  { value: "economy_mpv", label: "Economy MPV" },
  { value: "economy_van", label: "Economy Van" },
  { value: "business_van", label: "Business Van" },
  { value: "minibus", label: "Minibus" },
  { value: "bus", label: "Bus" },
];

const EMPTY: Omit<Offer, "id"> = {
  vehicle_category: "economy_van",
  car_model: "",
  from_label: "",
  to_label: "",
  from_lat: 30.325,
  from_lng: -9.413,
  to_lat: 31.629,
  to_lng: -7.981,
  from_iata: null,
  to_iata: null,
  max_passengers: 6,
  max_luggage: 6,
  price_amount: 100,
  price_currency: "EUR",
  distance_km: 250,
  duration_min: 180,
  active: true,
};

function FleetPage() {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState<Offer | (Omit<Offer, "id"> & { id?: string }) | null>(
    null,
  );

  const load = async () => {
    setBusy(true);
    const { data } = await supabase
      .from("transfers_offers")
      .select("*")
      .order("vehicle_category", { ascending: true })
      .order("price_amount", { ascending: true });
    setOffers((data as Offer[]) ?? []);
    setBusy(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const remove = async (id: string) => {
    if (!confirm("Delete this vehicle? It will disappear from /search results.")) return;
    const { error } = await supabase.from("transfers_offers").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Vehicle deleted · /search refreshed");
    void load();
  };

  const toggleActive = async (o: Offer) => {
    const { error } = await supabase
      .from("transfers_offers")
      .update({ active: !o.active })
      .eq("id", o.id);
    if (error) return toast.error(error.message);
    void load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-serif text-primary">Fleet Management</h2>
          <p className="text-sm text-muted-foreground">
            {offers.length} vehicle{offers.length === 1 ? "" : "s"} · changes reflect immediately in{" "}
            <code className="text-xs">/api/public/v1/transfers/search</code>
          </p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY })}>
          <Plus className="h-4 w-4" /> Add Vehicle
        </Button>
      </div>

      {busy ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : offers.length === 0 ? (
        <div className="border border-dashed rounded-xl bg-card py-20 text-center">
          <Car className="h-12 w-12 mx-auto mb-4 text-muted-foreground/60" />
          <h3 className="text-lg font-medium">No vehicles configured</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Add a vehicle to start receiving search matches.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {offers.map((o) => (
            <div
              key={o.id}
              className="border rounded-xl bg-card p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground font-mono">
                    {CATEGORIES.find((c) => c.value === o.vehicle_category)?.label ??
                      o.vehicle_category}
                  </div>
                  <h3 className="font-serif text-lg text-primary mt-1">{o.car_model}</h3>
                </div>
                <button
                  onClick={() => toggleActive(o)}
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    o.active
                      ? "bg-green-100 text-green-800"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {o.active ? "Active" : "Inactive"}
                </button>
              </div>
              <div className="text-sm text-muted-foreground mb-4 flex-1">
                <div className="truncate">→ {o.from_label}</div>
                <div className="truncate">↳ {o.to_label}</div>
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground mb-4">
                <span className="inline-flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" /> {o.max_passengers}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Briefcase className="h-3.5 w-3.5" /> {o.max_luggage}
                </span>
                <span>{Math.round(o.distance_km)} km</span>
                <span>{o.duration_min} min</span>
              </div>
              <div className="pt-3 border-t flex items-center justify-between">
                <span className="text-xl font-semibold text-primary">
                  {Number(o.price_amount).toFixed(0)} {o.price_currency}
                </span>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => setEditing(o)}
                    className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-xs border hover:bg-muted"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button
                    onClick={() => remove(o.id)}
                    className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-xs border border-destructive/30 text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <VehicleEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function VehicleEditor({
  initial,
  onClose,
  onSaved,
}: {
  initial: Offer | (Omit<Offer, "id"> & { id?: string });
  onClose: () => void;
  onSaved: () => void;
}) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isEdit = !!(f as Offer).id;

  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF({ ...f, [k]: v });

  const save = async () => {
    if (!f.car_model.trim() || !f.from_label.trim() || !f.to_label.trim()) {
      return toast.error("Model, From and To labels are required");
    }
    setSaving(true);
    const payload = {
      vehicle_category: f.vehicle_category as "economy_mpv" | "economy_van" | "business_van" | "minibus" | "bus",
      car_model: f.car_model.trim(),
      from_label: f.from_label.trim(),
      to_label: f.to_label.trim(),
      from_lat: Number(f.from_lat),
      from_lng: Number(f.from_lng),
      to_lat: Number(f.to_lat),
      to_lng: Number(f.to_lng),
      from_iata: f.from_iata?.toUpperCase() || null,
      to_iata: f.to_iata?.toUpperCase() || null,
      max_passengers: Number(f.max_passengers),
      max_luggage: Number(f.max_luggage),
      price_amount: Number(f.price_amount),
      price_currency: f.price_currency,
      distance_km: Number(f.distance_km),
      duration_min: Number(f.duration_min),
      active: f.active,
    };
    const { error } = isEdit
      ? await supabase.from("transfers_offers").update(payload).eq("id", (f as Offer).id)
      : await supabase.from("transfers_offers").insert(payload);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success(`Vehicle ${isEdit ? "updated" : "added"} · /search refreshed`);
    onSaved();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
          <DialogDescription>
            Updates appear immediately in the /api/public/v1/transfers/search endpoint
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Car model</Label>
            <Input
              value={f.car_model}
              onChange={(e) => set("car_model", e.target.value)}
              placeholder="Mercedes V-Class"
            />
          </div>
          <div className="col-span-2">
            <Label>API category</Label>
            <select
              value={f.vehicle_category}
              onChange={(e) => set("vehicle_category", e.target.value)}
              className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label} ({c.value})
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label>From label</Label>
            <Input value={f.from_label} onChange={(e) => set("from_label", e.target.value)} />
          </div>
          <div>
            <Label>To label</Label>
            <Input value={f.to_label} onChange={(e) => set("to_label", e.target.value)} />
          </div>
          <div>
            <Label>From IATA (optional)</Label>
            <Input
              value={f.from_iata ?? ""}
              maxLength={3}
              onChange={(e) => set("from_iata", e.target.value || null)}
              placeholder="AGA"
            />
          </div>
          <div>
            <Label>To IATA (optional)</Label>
            <Input
              value={f.to_iata ?? ""}
              maxLength={3}
              onChange={(e) => set("to_iata", e.target.value || null)}
              placeholder="RAK"
            />
          </div>

          <div>
            <Label>From lat</Label>
            <Input
              type="number"
              step="0.0001"
              value={f.from_lat}
              onChange={(e) => set("from_lat", Number(e.target.value))}
            />
          </div>
          <div>
            <Label>From lng</Label>
            <Input
              type="number"
              step="0.0001"
              value={f.from_lng}
              onChange={(e) => set("from_lng", Number(e.target.value))}
            />
          </div>
          <div>
            <Label>To lat</Label>
            <Input
              type="number"
              step="0.0001"
              value={f.to_lat}
              onChange={(e) => set("to_lat", Number(e.target.value))}
            />
          </div>
          <div>
            <Label>To lng</Label>
            <Input
              type="number"
              step="0.0001"
              value={f.to_lng}
              onChange={(e) => set("to_lng", Number(e.target.value))}
            />
          </div>

          <div>
            <Label>Max passengers</Label>
            <Input
              type="number"
              value={f.max_passengers}
              onChange={(e) => set("max_passengers", Number(e.target.value))}
            />
          </div>
          <div>
            <Label>Max luggage</Label>
            <Input
              type="number"
              value={f.max_luggage}
              onChange={(e) => set("max_luggage", Number(e.target.value))}
            />
          </div>
          <div>
            <Label>Distance (km)</Label>
            <Input
              type="number"
              value={f.distance_km}
              onChange={(e) => set("distance_km", Number(e.target.value))}
            />
          </div>
          <div>
            <Label>Duration (min)</Label>
            <Input
              type="number"
              value={f.duration_min}
              onChange={(e) => set("duration_min", Number(e.target.value))}
            />
          </div>
          <div>
            <Label>Price</Label>
            <Input
              type="number"
              value={f.price_amount}
              onChange={(e) => set("price_amount", Number(e.target.value))}
            />
          </div>
          <div>
            <Label>Currency</Label>
            <Input
              maxLength={3}
              value={f.price_currency}
              onChange={(e) => set("price_currency", e.target.value.toUpperCase())}
            />
          </div>

          <label className="col-span-2 inline-flex items-center gap-2 text-sm mt-2">
            <input
              type="checkbox"
              checked={f.active}
              onChange={(e) => set("active", e.target.checked)}
            />
            Active (visible in /search)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isEdit ? "Save" : "Add Vehicle"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
